import { faBuildings } from '@fortawesome/pro-regular-svg-icons';
import { faBolt, faLockOpen, IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cva } from 'class-variance-authority';
import { HTMLAttributes } from 'react';

import { cn } from '../utils/cn';

export type BrandBadgeType = 'enterprise' | 'pro' | 'proTrial' | 'proTeam' | 'team';

const containerStyles = cva(
  'bg-transparent select-none inline-flex h-4 w-auto items-center gap-[3px] rounded px-1 bg-gradient-to-r',
  {
    variants: {
      type: {
        pro: 'from-indigo-600/10 to-fuchsia-500/10 dark:from-indigo-700 dark:to-fuchsia-600',
        proTrial: 'from-indigo-600/10 to-fuchsia-500/10 dark:from-indigo-700 dark:to-fuchsia-600',
        proTeam: 'from-indigo-600/10 to-fuchsia-500/10 dark:from-indigo-700 dark:to-fuchsia-600',
        team: 'from-indigo-600/10 to-fuchsia-500/10 dark:from-indigo-700 dark:to-fuchsia-600',
        enterprise: 'from-blue-100 to-blue-50 dark:from-blue-700 dark:to-blue-500',
      },
    },
  },
);

const labelStyles = cva(
  'font-sans bg-clip-text text-transparent text-[10px] leading-none tracking-wide font-bold uppercase bg-gradient-to-r',
  {
    variants: {
      type: {
        pro: 'from-indigo-600 to-fuchsia-500 dark:from-white dark:to-fuchsia-200',
        proTrial: 'from-indigo-600 to-fuchsia-500 dark:from-white dark:to-fuchsia-200',
        proTeam: 'from-indigo-600 to-fuchsia-500 dark:from-white dark:to-fuchsia-200',
        team: 'from-indigo-600 to-fuchsia-500 dark:from-white dark:to-fuchsia-200',
        enterprise: 'from-blue-600 to-blue-500 dark:from-white dark:to-blue-100',
      },
    },
  },
);

const iconStyles = cva('size-[8px]', {
  variants: {
    type: {
      pro: 'text-fuchsia-500 dark:text-fuchsia-200',
      proTrial: 'text-fuchsia-500 dark:text-fuchsia-200',
      proTeam: 'text-fuchsia-500 dark:text-fuchsia-200',
      team: 'text-fuchsia-500 dark:text-fuchsia-200',
      enterprise: 'text-blue-500 dark:text-blue-200',
    },
  },
});

export interface BrandBadgeProps extends HTMLAttributes<HTMLButtonElement> {
  type: BrandBadgeType;
  theme?: 'light' | 'dark';
  isIconHidden?: boolean;
}

/**
 * A small, stylish tag that can be used to mark premium or professional content or users.
 * @see {@link https://stylus.design/pro-tag}
 */
export const BrandBadge = ({
  className,
  onClick,
  theme = 'light',
  type,
  isIconHidden = false,
}: BrandBadgeProps) => {
  const tagData: Record<BrandBadgeType, { icon: IconDefinition; label: string }> = {
    pro: {
      icon: faBolt,
      label: 'Pro',
    },
    proTrial: {
      icon: faLockOpen,
      label: 'Pro',
    },
    proTeam: {
      icon: faBolt,
      label: 'Pro Team',
    },
    team: {
      icon: faBolt,
      label: 'Team',
    },
    enterprise: {
      icon: faBuildings,
      label: 'Ent',
    },
  };

  const Wrapper = onClick ? 'button' : 'span';

  return (
    <Wrapper
      onClick={onClick}
      className={cn(containerStyles({ type }), className)}
      data-theme={theme}
      data-testid='brand-badge'
    >
      <span className={labelStyles({ type })}>{tagData[type].label}</span>
      {!isIconHidden && (
        <FontAwesomeIcon
          data-testid='brand-badge-icon'
          icon={tagData[type].icon}
          className={iconStyles({ type })}
          size='xs'
        />
      )}
    </Wrapper>
  );
};
